import React, { useRef, useEffect, useState } from "react";
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
} from 'recharts';
import { BarChartLegend } from "./snippets";
import CustomScrollbar from "../scrollbar";

const CustomBarChart = (props) => {
    const { data = [], xdataKey, ydataKey, ydataKey2, ydataKey3, showLine = true, barSize = 20, noOfBars = 1,barGap=20 } = props
    const chartRef = useRef(null);
    const scrollRef = useRef(null);
    const chartHeight = 400;
    const [containerWidth, setContainerWidth] = useState(0);
    const [chartWidth, setChartWidth] = useState(0);
    const { name1 = "Net Amount", name2 = "", name3 = "" } = props
    const { color1 = "#00689D", color2 = "#E0A833", color3 = "#E0A833" } = props
    const [legendData, setLegendData]=useState([])
    const [barWidth, setBarWidth] = useState((barSize * noOfBars) + barGap)
    const updateChartWidth = () => {
        const gridEl = chartRef.current.querySelector('.recharts-cartesian-grid');
        if (gridEl) {
            const gridRect = gridEl.getBoundingClientRect();
            const containerWidth = gridRect.width;
            const calculatedWidth = Math.max(
                containerWidth,
                data.length * barWidth
            );
            setContainerWidth(containerWidth);
            setChartWidth(calculatedWidth);
        }
    };
    useEffect(() => {
      if (name1) {
        const legendData = [
          {
            name: name1,
            color: color1,
            active: true,
          },
        ]
        if (name2) {
          legendData.push({
            name: name2,
            color: color2,
            active: true,
          })
        }
        if (name3) {
          legendData.push({
            name: name3,
            color: color3,
            active: true,
          })
        }
        setLegendData(legendData)
      }
    }, [name1])
    useEffect(() => {
      const noOfBars = showLine ? legendData.length : legendData.filter((item) => item.active).length
      const barWidth = barSize * noOfBars + barGap
      setBarWidth(barWidth)
      updateChartWidth()
    }, [legendData, data])

    useEffect(() => {
      const observer = new MutationObserver(() => {
        const gridEl = chartRef.current?.querySelector('.recharts-cartesian-grid')
        if (gridEl) {
          updateChartWidth()
          observer.disconnect()
        }
      })
      if (chartRef.current) {
        observer.observe(chartRef.current, {
          childList: true,
          subtree: true,
        })
      }
      return () => observer.disconnect()
    }, [])
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                position: "relative",
                height: chartHeight,
                margin: "20px 0",
            }}
        >
            <div
                ref={chartRef}
                style={{
                    position: "absolute",
                    overflow: "hidden",
                    height: chartHeight,
                    width: "100%",
                }}
            >
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <ComposedChart data={data}>
                        <CartesianGrid stroke="#e0e0e0" vertical={false} layer="below" />
                        <XAxis
                            dataKey={xdataKey}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                            tick={() => <></>}
                        />
                        <YAxis yAxisId="left" axisLine={false}
                            tickLine={false} tickCount={10} domain={[0, 'auto']} />
                        <YAxis
                            yAxisId="right"
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            tickCount={10}
                            allowDecimals={false}
                            tick={showLine ? undefined : { fill: 'transparent' }}
                            label={showLine ? undefined : { fill: 'transparent' }} />
                        <Bar yAxisId="left" dataKey={ydataKey} barSize={0} fill={'transparent'} name={name1} />
                        <Line
                            yAxisId="right"
                            type="linear"
                            dataKey={ydataKey2}
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            name={name2}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {containerWidth && (
                <div style={{ width: containerWidth }} className="chart-scrollbar" ref={scrollRef}>
                    <div
                        style={{
                            width: chartWidth,
                            transition: "transform 0.3s ease-in-out",
                            height: '100%',
                            zIndex: 1
                        }}
                    >
                        <ResponsiveContainer width={chartWidth} height={chartHeight}>
                            <ComposedChart data={data}>
                                <XAxis
                                    dataKey={xdataKey}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                    tickLine={false}
                                />

                                <YAxis yAxisId="left" axisLine={false} tickLine={false} tickCount={10} domain={[0, 'auto']} hide />
                                <Tooltip />
                                {showLine && (
                                  <YAxis
                                    yAxisId="right"
                                    axisLine={false}
                                    tickLine={false}
                                    orientation="right"
                                    tickCount={10}
                                    allowDecimals={false}
                                    tick={showLine ? undefined : { fill: 'transparent' }}
                                    label={showLine ? undefined : { fill: 'transparent' }}
                                    hide
                                  />
                                )}
                                {legendData?.[0]?.active &&<Bar yAxisId="left" dataKey={ydataKey} barSize={barSize} barGap={barWidth - barSize} fill={color1} name={name1} />}
                                {showLine && legendData?.[1]?.active &&<Line
                                    yAxisId="right"
                                    type="linear"
                                    dataKey={ydataKey2}
                                    stroke={color2}
                                    strokeWidth={3}
                                    dot={{
                                        r: 6,
                                        fill: color2,
                                        stroke: '#fff',
                                        strokeWidth: 2
                                    }}
                                    name={name2}
                                />}
                                {noOfBars > 1 && legendData?.[1]?.active && <Bar yAxisId="left" dataKey={ydataKey2} barSize={barSize} barGap={barWidth - barSize} fill={color2} name={name2} />}
                                {noOfBars > 2 && legendData?.[2]?.active && <Bar yAxisId="left" dataKey={ydataKey3} barSize={barSize} barGap={barWidth - barSize} fill={color3} name={name3} />}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            {Math.abs(chartWidth - containerWidth) > 10 && <CustomScrollbar scrollTargetRef={scrollRef} />}
            <BarChartLegend data={legendData} setData={setLegendData} />
        </div>
    );
};

export default React.memo(CustomBarChart);
